import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import extractText from 'flarum/common/utils/extractText';

import revealablePassword from '../utils/passwordReveal';
import { PASSWORD_RULES, PASSWORD_RULE_KEYS, passwordMeetsPolicy } from '../utils/passwordPolicy';

/**
 * «Cambiar contraseña» de Configuración, de verdad (PKG-FORO-PASSWORD-SETTINGS-UX).
 *
 * El modal del core con ese mismo botón NO cambiaba nada: mandaba el correo de recuperación y
 * se cerraba sin decir palabra — reportado LIVE como «guardó pero sin confirmación», porque la
 * persona acababa cambiándola por el enlace y ninguna pantalla le confirmó nada. Este modal
 * hace el cambio aquí mismo contra `POST /api/umbral/password` (ver el controlador para la
 * politica de sesiones) y cubre los cuatro huecos del reporte:
 *
 *   a) la política se enuncia ANTES de escribir (la misma lista viva de `/reset`),
 *   b) el éxito se dice: toast de la bandeja global + el modal se cierra,
 *   c) el «no» de la política aparece INLINE junto al campo, no en un bloque genérico,
 *   d) cada campo lleva su ojo mostrar/ocultar.
 *
 * La contraseña ACTUAL se pide por lo mismo que el core la pide para cambiar el correo: una
 * sesión abierta en un equipo ajeno no debe bastar para dejar al dueño fuera. Y como pedirla
 * deja fuera a quien no la recuerda, el pie conserva el camino del core: un enlace que manda
 * el correo de recuperación (`POST /api/forgot`, el mismo endpoint del modal reemplazado).
 */
type Field = 'current' | 'next' | 'confirm';

const FIELD_ORDER: Field[] = ['current', 'next', 'confirm'];

/** `autocomplete` correcto por campo: el gestor de contraseñas rellena la actual y OFRECE nueva. */
const AUTOCOMPLETE: Record<Field, string> = {
  current: 'current-password',
  next: 'new-password',
  confirm: 'new-password',
};

export default class CambiarPasswordModal extends Modal<any> {
  values: Record<Field, string> = { current: '', next: '', confirm: '' };

  /** Ojo por campo. Siempre nace oculto: es un modal, no un borrador que se retoma. */
  shown: Record<Field, boolean> = { current: false, next: false, confirm: false };

  errors: Partial<Record<Field, string>> = {};

  /** Mensajes del servidor sin campo al que anclarse; se pintan enteros arriba. */
  unmapped: string[] = [];

  loading = false;

  /** Candado propio del enlace «te enviamos el correo» del pie. */
  sendingForgot = false;

  className() {
    return 'Modal--small CmPasswordModal';
  }

  title() {
    return this.t('title');
  }

  content() {
    return (
      <div className="Modal-body">
        <p className="CmPassword-sub">{this.t('body')}</p>

        {this.unmapped.map((message) => (
          <div className="CmPassword-errorbar" role="alert">
            {message}
          </div>
        ))}

        {this.field('current')}
        {this.field('next', this.requirements())}
        {this.field('confirm')}

        <Button className="Button Button--primary Button--block CmPassword-submit" type="submit" loading={this.loading}>
          {this.t('submit')}
        </Button>

        {/* El camino que el modal del core ofrecía en exclusiva, conservado como salida:
            sin la actual no hay cambio directo, pero sí el enlace de recuperación. */}
        <button type="button" className="CmPassword-forgot" disabled={this.sendingForgot} onclick={this.sendForgot.bind(this)}>
          {this.t('forgot')}
        </button>
      </div>
    );
  }

  /**
   * a) del paquete — la política dicha ANTES de enviar, pintada en vivo como en `/reset`:
   * cada requisito se marca en cuanto lo escrito lo cumple. Con el campo vacío, ninguno.
   */
  requirements() {
    const value = this.values.next;

    return (
      <div className="CmPassword-reqsWrap">
        <p className="CmPassword-reqsTitle">{this.t('requirements_title')}</p>
        <ul className="CmPassword-reqs" id="cm-pw-reqs">
          {PASSWORD_RULE_KEYS.map((key) => (
            <li data-rule={key} data-ok={value !== '' && PASSWORD_RULES[key](value) ? '1' : '0'}>
              {this.t('req_' + key)}
            </li>
          ))}
        </ul>
        <p className="CmPassword-note" id="cm-pw-note">
          {this.t('req_note')}
        </p>
      </div>
    );
  }

  field(name: Field, extra: any = null) {
    const error = this.errors[name];
    const describedBy =
      [error ? 'cm-pw-err-' + name : null, name === 'next' ? 'cm-pw-reqs cm-pw-note' : null].filter(Boolean).join(' ') || undefined;

    return (
      <div className={'CmPassword-field' + (error ? ' CmPassword-field--error' : '')} data-cm-pw-field={name}>
        <label htmlFor={'cm-pw-' + name}>{this.t('label_' + name)}</label>
        {revealablePassword(
          {
            id: 'cm-pw-' + name,
            className: 'FormControl CmPassword-input',
            name: name,
            autocomplete: AUTOCOMPLETE[name],
            value: this.values[name],
            disabled: this.loading,
            'aria-invalid': error ? 'true' : undefined,
            'aria-describedby': describedBy,
            oninput: (e: any) => {
              this.values[name] = e.target.value;
              // El error se borra al escribir: no se regaña a quien ya está corrigiendo.
              delete this.errors[name];
            },
          },
          this.shown[name],
          () => {
            this.shown[name] = !this.shown[name];
          }
        )}
        {extra}
        {error ? (
          <p className="CmPassword-msg" id={'cm-pw-err-' + name} data-cm-pw-error={name}>
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  onsubmit(e: SubmitEvent) {
    e.preventDefault();

    if (this.loading) return;

    this.unmapped = [];
    this.errors = this.validate();

    if (Object.keys(this.errors).length) {
      this.focusFirstError();
      return;
    }

    this.loading = true;

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/umbral/password',
        body: { data: { attributes: { currentPassword: this.values.current, password: this.values.next } } },
        errorHandler: this.onerror.bind(this),
      })
      .then(
        () => {
          // b) del paquete — el éxito SE DICE. El aviso va a la bandeja global (no al modal,
          // que se cierra) y no hay redirect ninguno: el servidor ya re-emitió el token de
          // ESTA sesión, así que la SPA sigue donde estaba y con la sesión viva.
          this.hide();
          app.alerts.show({ type: 'success', title: this.t('success_title') }, this.t('success_body'));
        },
        () => {
          this.loading = false;
          m.redraw();
        }
      );
  }

  /** El espejo de cliente: mismos textos que devolvería el servidor, sin ida y vuelta. */
  validate(): Partial<Record<Field, string>> {
    const errors: Partial<Record<Field, string>> = {};

    if (!this.values.current) errors.current = this.extract('errors.current_required');

    if (!this.values.next) errors.next = this.extract('errors.password_required');
    else if (!passwordMeetsPolicy(this.values.next)) errors.next = this.extract('errors.password_policy');

    if (this.values.confirm !== this.values.next) errors.confirm = this.extract('errors.password_confirm');

    return errors;
  }

  /**
   * c) del paquete — el «no» del servidor, junto a su campo. Los punteros son los del
   * controlador: `currentPassword` (actual incorrecta) y `password` (política). Lo que no se
   * reconozca se muestra entero arriba: perder un mensaje sería peor que enseñarlo suelto.
   */
  onerror(error: any) {
    // Primero soltar el candado: con `loading` puesto los campos están `disabled` y el foco
    // del final no entraría (mismo hallazgo que documenta `RegistroPage.onerror`).
    this.loading = false;

    const list: any[] = (error && error.response && error.response.errors) || [];
    const errors: Partial<Record<Field, string>> = {};
    const unmapped: string[] = [];

    list.forEach((item) => {
      const pointer: string = (item && item.source && item.source.pointer) || '';
      const detail: string = (item && item.detail) || '';

      if (pointer === '/data/attributes/currentPassword') errors.current = detail || this.extract('errors.current_password');
      else if (pointer === '/data/attributes/password') errors.next = detail || this.extract('errors.password_policy');
      else if (detail) unmapped.push(detail);
    });

    this.errors = errors;
    this.unmapped = unmapped;

    // Un error sin detalle útil (sesión caducada, red caída) no puede quedar en silencio: se
    // deja que la alerta por defecto de Flarum lo cuente — es la que explica el CSRF caducado
    // («Has estado inactivo…») con la instrucción de recargar.
    if (!Object.keys(errors).length && !unmapped.length) {
      if (error && error.alert) app.alerts.show(error.alert, error.alert.content);
      return;
    }

    this.focusFirstError();
  }

  focusFirstError() {
    m.redraw.sync();

    const first = FIELD_ORDER.find((name) => this.errors[name] !== undefined);

    if (!first) return;

    const input = document.querySelector(`[data-cm-pw-field="${first}"] input`) as HTMLElement | null;

    if (input) input.focus();
  }

  /** El envío del correo de recuperación que hacía el modal del core, con confirmación. */
  sendForgot() {
    if (this.sendingForgot || !app.session.user) return;

    this.sendingForgot = true;

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/forgot',
        body: { email: app.session.user.email() },
      })
      .then(
        () => {
          this.hide();
          app.alerts.show({ type: 'success', title: this.t('forgot_sent_title') }, this.t('forgot_sent_body'));
        },
        () => {
          this.sendingForgot = false;
          m.redraw();
        }
      );
  }

  t(key: string) {
    return app.translator.trans('umbral-comunidad.forum.password_settings.' + key);
  }

  /** Como cadena: los mensajes de error van dentro de `<p>` y se comparan en las specs. */
  extract(key: string): string {
    return extractText(this.t(key));
  }
}
